/**
 * Flattens Fabric annotations into a PDF.
 *
 * The annotations were drawn on an overlay sized to pdf.js's render, which honors the page's
 * /Rotate and sizes itself from the crop box. The PDF library draws into unrotated user space and
 * honors neither, so every coordinate is mapped back through util/pdfPageGeometry and the page
 * rotation is handed to the draw calls so the burned content reads upright.
 *
 * The PDF library is injected rather than imported: it is loaded from a CDN at runtime, and taking
 * it as a parameter keeps this module unit-testable.
 */

import {
    burnRotationDegrees,
    FABRIC_FONT_SIZE_MULT,
    fabricOffsetToPdf,
    normalizePageRotation,
    resolvePageViewBox,
} from './pdfPageGeometry';

export interface AnnotationState {
    objects: any[];
}

export interface BurnOptions {
    /**
     * fabric's `Text.prototype._fontSizeMult`. A line's first baseline sits this multiple of the
     * font size below the object's top, and lines are spaced by it as well.
     */
    fontSizeMult?: number;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
    return result
        ? {
              r: parseInt(result[1], 16) / 255,
              g: parseInt(result[2], 16) / 255,
              b: parseInt(result[3], 16) / 255,
          }
        : { r: 0, g: 0, b: 0 };
}

/**
 * Re-wrap a textbox's content to the box width, matching what fabric showed on the canvas. Long
 * words that do not fit are broken by character.
 */
function wrapTextToBox(textContent: string, boxWidth: number, fontSize: number, font: any): string[] {
    const lines: string[] = [];

    for (const inputLine of textContent.split('\n')) {
        const lineWidth = font.widthOfTextAtSize(inputLine, fontSize);
        if (lineWidth <= boxWidth || inputLine.length === 0) {
            lines.push(inputLine);
            continue;
        }

        const words = inputLine.split(' ');
        let currentLine = '';
        for (const word of words) {
            const testLine = currentLine ? currentLine + ' ' + word : word;
            if (font.widthOfTextAtSize(testLine, fontSize) <= boxWidth) {
                currentLine = testLine;
                continue;
            }

            if (currentLine) lines.push(currentLine);

            // Break long words by character if wider than box
            if (font.widthOfTextAtSize(word, fontSize) > boxWidth) {
                let partial = '';
                for (const char of word) {
                    if (partial && font.widthOfTextAtSize(partial + char, fontSize) > boxWidth) {
                        lines.push(partial);
                        partial = char;
                    } else {
                        partial += char;
                    }
                }
                currentLine = partial;
            } else {
                currentLine = word;
            }
        }
        if (currentLine) lines.push(currentLine);
    }

    return lines;
}

/**
 * Burn the given per-page annotations into `pdfBytes` and return the modified document.
 *
 * `scale` is the pdf.js viewport scale the overlay canvas was rendered at.
 */
export async function burnAnnotationsIntoPdf(
    pdfLib: any,
    pdfBytes: Uint8Array,
    pageAnnotations: Record<number, AnnotationState>,
    scale: number,
    options: BurnOptions = {}
): Promise<Uint8Array> {
    const { PDFDocument, rgb, StandardFonts, degrees } = pdfLib;
    const fontSizeMult = options.fontSizeMult || FABRIC_FONT_SIZE_MULT;

    const pdfLibDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });

    // Note: Canvas uses Arial but PDF embeds Helvetica (closest standard PDF font).
    // Minor glyph width differences may cause slight wrapping variations in the PDF output.
    const helveticaFont = await pdfLibDoc.embedFont(StandardFonts.Helvetica);
    const pages = pdfLibDoc.getPages();

    for (let pageNum = 1; pageNum <= pages.length; pageNum++) {
        if (!pageAnnotations[pageNum]) continue;

        const page = pages[pageNum - 1];
        const annotations = pageAnnotations[pageNum];

        const rotation = normalizePageRotation(page.getRotation().angle);
        const view = resolvePageViewBox(page);

        // Canvas pixels to PDF points
        const inv = 1 / scale;

        for (const obj of annotations.objects || []) {
            // Skip objects marked as non-exportable (e.g., crop overlay/rect)
            if (obj.excludeFromExport === true) continue;

            const objScaleX = obj.scaleX || 1;
            const objScaleY = obj.scaleY || 1;
            // Cancel the viewer's clockwise page turn, and carry the object's own turn through.
            const burnRotation = burnRotationDegrees(rotation, obj.angle);

            switch (obj.type) {
                case 'i-text':
                case 'text':
                case 'textbox': {
                    const textColor = hexToRgb(obj.fill || '#000000');
                    const fontSizePx = (obj.fontSize || 16) * objScaleY;
                    const fontSize = fontSizePx * inv;
                    const lineHeight = obj.lineHeight || 1.16;
                    // Replace tabs with spaces, remove other unsupported control characters
                    const textContent = (obj.text || '')
                        .replace(/\t/g, '    ')
                        // eslint-disable-next-line no-control-regex
                        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

                    const lines =
                        obj.type === 'textbox' && obj.width
                            ? wrapTextToBox(textContent, (obj.width || 300) * objScaleX * inv, fontSize, helveticaFont)
                            : textContent.split('\n');

                    for (let index = 0; index < lines.length; index++) {
                        const baselineOffset = fontSizeMult * fontSizePx * (1 + index * lineHeight);
                        const anchor = fabricOffsetToPdf(obj, 0, baselineOffset, scale, rotation, view);
                        page.drawText(lines[index], {
                            x: anchor.x,
                            y: anchor.y,
                            size: fontSize,
                            font: helveticaFont,
                            color: rgb(textColor.r, textColor.g, textColor.b),
                            rotate: degrees(burnRotation),
                        });
                    }
                    break;
                }

                case 'rect': {
                    const rectWidthPx = (obj.width || 0) * objScaleX;
                    const rectHeightPx = (obj.height || 0) * objScaleY;
                    const rectStrokeColor = hexToRgb(obj.stroke || '#000000');
                    // The rect path runs from the anchor along its local +x/+y, which is display
                    // right/up once rotated, so anchor on the box's on-screen bottom-left.
                    const rectAnchor = fabricOffsetToPdf(obj, 0, rectHeightPx, scale, rotation, view);
                    const rectOptions: any = {
                        x: rectAnchor.x,
                        y: rectAnchor.y,
                        width: rectWidthPx * inv,
                        height: rectHeightPx * inv,
                        borderColor: rgb(rectStrokeColor.r, rectStrokeColor.g, rectStrokeColor.b),
                        borderWidth: (obj.strokeWidth || 2) * objScaleX * inv,
                        rotate: degrees(burnRotation),
                    };

                    if (obj.fill && obj.fill !== 'transparent') {
                        const rectFillColor = hexToRgb(obj.fill);
                        rectOptions.color = rgb(rectFillColor.r, rectFillColor.g, rectFillColor.b);
                    }

                    page.drawRectangle(rectOptions);
                    break;
                }
            }
        }
    }

    return new Uint8Array(await pdfLibDoc.save());
}
